import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, Grid, Modal, Typography } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import Swal from 'sweetalert2';
import { ImageUploadServices } from './imageUploadSrevices';


const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2 MB in bytes

// const MAX_FILE_SIZE = 200 * 1024;  // 200 KB in bytes

export default function ImgUploadDialog(props) {
    const {
        open, onClose, imgData, imgUploadResponse, trigStatus,
        maxFileSize,
        expectedWidth,
        expectedHeight,
        expectedAspectRatio,
        // Allow tolerance in pixels for dimensions (optional)
        dimensionTolerance = 5,
        // Allow tolerance for aspect ratio (optional)
        aspectRatioTolerance = 0.05,
        allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
        allowedTypesErrorMsg = 'File type not supported. Please upload a PNG, JPEG, JPG, or WebP image.'
    } = props;
    const { control, handleSubmit, setValue, formState: { errors }, watch, reset, clearErrors } = useForm({
        mode: "onChange",
        defaultValues: {
            folder: imgData.path,
            file: '',
        }
    });
    const handleCLose = () => {
        onClose();
        reset();
    }
    useEffect(() => {
        reset({
            folder: imgData.path,
            file: '',
        });
    }, [imgData.path, reset])
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [image, setImage] = useState("");
    const [imgErr, setImgErr] = useState("");
    const [imageMetadata, setImageMetadata] = useState(null);

    useEffect(() => {
        setImage("");
        clearErrors();
    }, [open])

    // Function to validate image dimensions and aspect ratio
    const validateImageDimensions = (file) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const width = img.width;
                const height = img.height;
                const actualAspectRatio = width / height;

                // Store metadata for potential error messages
                const metadata = { width, height, aspectRatio: actualAspectRatio };
                setImageMetadata(metadata);

                let errorMessage = '';

                // Check dimensions if they are provided
                if (expectedWidth && expectedHeight) {
                    const widthInRange = Math.abs(width - expectedWidth) <= dimensionTolerance;
                    const heightInRange = Math.abs(height - expectedHeight) <= dimensionTolerance;

                    if (!widthInRange || !heightInRange) {
                        errorMessage = `Image dimensions (${width}x${height}) do not match expected dimensions (${expectedWidth}x${expectedHeight})`;
                    }
                }

                // Check aspect ratio if it is provided
                if (expectedAspectRatio && !errorMessage) {
                    const aspectRatioDiff = Math.abs(actualAspectRatio - expectedAspectRatio);

                    if (aspectRatioDiff > aspectRatioTolerance) {
                        errorMessage = `Image aspect ratio (${actualAspectRatio.toFixed(2)}) does not match expected ratio (${expectedAspectRatio.toFixed(2)})`;
                    }
                }

                if (errorMessage) {
                    reject(errorMessage);
                } else {
                    resolve(true);
                }
            };

            img.onerror = () => {
                reject('Failed to load image for validation');
            };

            img.src = URL.createObjectURL(file);
        });
    };

    const onSelectedImage = (event, onChange) => {
        const file = event.target.files[0];
        if (file) {
            setImage(URL.createObjectURL(file));
            onChange(file);
        } else {
            console.log("no file selected")
            setImage("");
            onChange(null);
        }

    };


    const handleUpload = async (data) => {
        setIsSubmitting(true);
        // console.log("submitted data:",data)
        if (!data.file || !data.folder) {
            // console.error("File or folder is missing");
            return;
        }

        const formData = new FormData();
        formData.append('file', data.file);
        formData.append('folder', data.folder);

        try {
            const imageUploadResponse = await ImageUploadServices.uploadImage(formData);
            if (imageUploadResponse.success == '1') {
                imgUploadResponse(imageUploadResponse.data);
                setIsSubmitting(false);
                // Swal.fire(imageUploadResponse.message,'','success');     
            } else {
                Swal.fire('Image upload Failed', '', 'error');
                setIsSubmitting(false);
                onClose();
            }

        } catch (error) {
            // console.error("Image Upload failed:", error);
        } finally {
            onClose();
            setIsSubmitting(false);
            reset();
        }
    };
    return (
        <div>
            <Modal
                open={open}
                // onClose={() => setOpenServiceDropdown(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{ overflow: "scroll" }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: { xs: '80%', sm: '50%' },
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        p: 0,
                        width: { xs: "95%", sm: "25%" },
                        minHeight: '25%'
                    }}

                    borderRadius={2}
                >
                    <Box sx={{ borderBottom: '1px solid lightGray', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '3%' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '1rem' }} >
                            Image Upload
                        </Typography>
                        <IconButton
                            size='small'
                            onClick={() => handleCLose()}
                            sx={{ p: 0 }}
                            color='error'
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box component="form" onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleSubmit(handleUpload)(e); }}>

                        <Box dividers sx={{ width: '100%', height: '300px' ,py:'3%'}}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} >
                                    {image && (
                                        <Box sx={{ width: '50%', m: 'auto' }}>
                                            {watch('file') && watch('file').type && watch('file').type.startsWith('video/') ? (
                                                <video
                                                    style={{
                                                        maxWidth: '100%',
                                                        marginTop: "20px",
                                                        marginLeft: "5px",
                                                        borderRadius: "10px", maxHeight: '100%'
                                                    }}
                                                    controls
                                                    src={image}
                                                />
                                            ) : (
                                                <img
                                                    style={{
                                                        maxWidth: '50%',
                                                        marginTop: "20px",
                                                        marginLeft: "5px",
                                                        borderRadius: "10px", maxHeight: '100%'
                                                    }}
                                                    alt="Preview Image"
                                                    src={image}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </Grid>
                                <Grid item xs={12} sx={{textAlign:'center'}}>
                                    <Controller
                                        control={control}
                                        name="file"
                                        rules={{
                                            required: 'Please select an image/file',
                                            validate: {
                                                fileType: (value) => {
                                                    if (value) {
                                                        return allowedTypes.includes(value.type) || allowedTypesErrorMsg;
                                                    }
                                                    return true;
                                                },
                                                fileSize: (value) => {
                                                    if (value) {
                                                        if (maxFileSize) {
                                                            return value.size <= maxFileSize || `File size should be less than ${maxFileSize / (1024)} KB.`;
                                                        } else {
                                                            return value.size <= MAX_FILE_SIZE || `File size should be less than ${MAX_FILE_SIZE / (1024)} KB.`;
                                                        }
                                                    }
                                                    return true;
                                                },
                                                dimensions: async (value) => {
                                                    if (value && !value.type.startsWith('video/') && (expectedWidth || expectedHeight || expectedAspectRatio)) {
                                                        try {
                                                            await validateImageDimensions(value);
                                                            return true;
                                                        } catch (error) {
                                                            return error;
                                                        }
                                                    }
                                                    return true;
                                                }
                                            }
                                        }}
                                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <>
                                                <input
                                                    type="file"
                                                    accept={allowedTypes.join(',')}
                                                    id="file"
                                                    onChange={(event) => {
                                                        const file = event.target.files[0];
                                                        // console.log("file:", file);
                                                        onChange(file);
                                                        onSelectedImage(event, onChange);
                                                    }}
                                                    style={{ display: 'none' }}

                                                />

                                                <Box
                                                    component="label"
                                                    htmlFor="file"
                                                >
                                                    <Button sx={{ }} size='small' variant="outlined" component="span" disabled={isSubmitting}>
                                                        Choose Image
                                                    </Button>
                                                </Box>
                                                {error && (
                                                    <Typography variant="caption" color="error">
                                                        {error.message}
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: '.5%' ,borderTop:'1px solid lightGray',py:'2%'}}>
                            <Button variant='outlined' size='small' type='submit' disabled={errors?.file || isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <CircularProgress size={24} sx={{ marginRight: 1 }} />
                                        Uploading...
                                    </>
                                ) : (
                                    'Upload Image'
                                )}
                            </Button>
                            <Button variant='outlined' size='small' onClick={()=>handleCLose()} color="error" disabled={isSubmitting}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};


// import React, { useEffect, useState } from 'react';
// import Button from '@mui/material/Button';
// import Dialog from '@mui/material/Dialog';
// import DialogTitle from '@mui/material/DialogTitle';
// import DialogContent from '@mui/material/DialogContent';
// import DialogActions from '@mui/material/DialogActions';
// import IconButton from '@mui/material/IconButton';
// import CloseIcon from '@mui/icons-material/Close';
// import { Box, CircularProgress, Grid, Typography } from '@mui/material';
// import { useForm, Controller } from 'react-hook-form';
// import Swal from 'sweetalert2';
// import { ImageUploadServices } from './imageUploadSrevices';

// const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB in bytes
// const MIN_WIDTH = 100; // Minimum width in pixels
// const MIN_HEIGHT = 100; // Minimum height in pixels
// const MAX_WIDTH = 2000; // Maximum width in pixels
// const MAX_HEIGHT = 2000; // Maximum height in pixels

// export default function ImgUploadDialog({ 
//     open, 
//     onClose, 
//     imgData, 
//     imgUploadResponse,
//     minWidth = MIN_WIDTH,
//     minHeight = MIN_HEIGHT,
//     maxWidth = MAX_WIDTH,
//     maxHeight = MAX_HEIGHT,
//     maxFileSize = MAX_FILE_SIZE 
// }) {
//     const { control, handleSubmit, formState: { errors }, reset } = useForm({
//         mode: "onChange",
//         defaultValues: {
//             folder: imgData?.path || '',
//             file: '',
//         }
//     });
//     const [isSubmitting, setIsSubmitting] = useState(false);
//     const [image, setImage] = useState("");

//     useEffect(() => {
//         setImage("");
//     }, [open]);

//     const validateImageDimensions = (file) => {
//         return new Promise((resolve) => {
//             const img = new Image();
//             img.src = URL.createObjectURL(file);
//             img.onload = () => {
//                 URL.revokeObjectURL(img.src);
//                 if (img.width < minWidth || img.height < minHeight) {
//                     resolve(`Image dimensions must be at least ${minWidth}x${minHeight} pixels`);
//                 } else if (img.width > maxWidth || img.height > maxHeight) {
//                     resolve(`Image dimensions must not exceed ${maxWidth}x${maxHeight} pixels`);
//                 } else {
//                     resolve(true);
//                 }
//             };
//             img.onerror = () => {
//                 URL.revokeObjectURL(img.src);
//                 resolve('Error loading image');
//             };
//         });
//     };

//     const onSelectedImage = async (event, onChange) => {
//         const file = event.target.files[0];
//         if (file) {
//             const validationResult = await validateImageDimensions(file);
//             if (validationResult === true) {
//                 setImage(URL.createObjectURL(file));
//                 onChange(file);
//             } else {
//                 setImage("");
//                 onChange(null);
//                 Swal.fire({
//                     title: 'Invalid Image Dimensions',
//                     text: validationResult,
//                     icon: 'error'
//                 });
//             }
//         }
//     };

//     const handleUpload = async (data) => {
//         setIsSubmitting(true);
//         if (!data.file || !data.folder) {
//             setIsSubmitting(false);
//             return;
//         }

//         const formData = new FormData();
//         formData.append('file', data.file);
//         formData.append('folder', data.folder);

//         try {
//             const imageUploadResponse = await ImageUploadServices.uploadImage(formData);
//             if (imageUploadResponse.success === '1') {
//                 imgUploadResponse(imageUploadResponse.data);
//                 setIsSubmitting(false);
//             } else {
//                 Swal.fire('Image upload Failed', '', 'error');
//                 setIsSubmitting(false);
//                 onClose();
//             }
//         } catch (error) {
//             Swal.fire('Image upload Failed', '', 'error');
//             setIsSubmitting(false);
//         } finally {
//             onClose();
//             setIsSubmitting(false);
//             reset();
//         }
//     };

//     return (
//         <Dialog open={open} maxWidth="lg">
//             <DialogTitle sx={{ m: 0, p: 2 }}>
//                 Image Upload
//                 <IconButton
//                     aria-label="close"
//                     onClick={onClose}
//                     sx={{
//                         position: 'absolute',
//                         right: 8,
//                         top: 8,
//                         color: (theme) => theme.palette.grey[500],
//                     }}
//                 >
//                     <CloseIcon />
//                 </IconButton>
//             </DialogTitle>
//             <Box component="form" onSubmit={handleSubmit(handleUpload)}>
//                 <DialogContent dividers sx={{ width: '420px', height: '300px' }}>
//                     <Grid container spacing={2}>
//                         <Grid item xs={12}>
//                             <Controller
//                                 control={control}
//                                 name="file"
//                                 rules={{
//                                     required: 'Please select an image',
//                                     validate: {
//                                         fileType: (value) => {
//                                             if (value) {
//                                                 const acceptedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
//                                                 return acceptedTypes.includes(value.type) || 'File type not supported. Please upload a PNG, JPEG, JPG, or WebP image.';
//                                             }
//                                             return true;
//                                         },
//                                         fileSize: (value) => {
//                                             if (value) {
//                                                 return value.size <= maxFileSize || `File size should be less than ${maxFileSize / (1024 * 1024)} MB.`;
//                                             }
//                                             return true;
//                                         }
//                                     }
//                                 }}
//                                 render={({ field: { onChange }, fieldState: { error } }) => (
//                                     <>
//                                         <input
//                                             type="file"
//                                             accept="image/*"
//                                             id="file"
//                                             onChange={(event) => onSelectedImage(event, onChange)}
//                                             style={{ display: 'none' }}
//                                         />
//                                         <Box component="label" htmlFor="file">
//                                             <Button 
//                                                 sx={{ width: "100%" }} 
//                                                 variant="outlined" 
//                                                 component="span" 
//                                                 disabled={isSubmitting}
//                                             >
//                                                 Choose Image
//                                             </Button>
//                                         </Box>
//                                         {error && (
//                                             <Typography variant="caption" color="error">
//                                                 {error.message}
//                                             </Typography>
//                                         )}
//                                     </>
//                                 )}
//                             />
//                         </Grid>
//                         <Grid item xs={12}>
//                             {image && (
//                                 <Box
//                                     component="img"
//                                     sx={{
//                                         width: 100,
//                                         marginTop: "20px",
//                                         marginLeft: "5px",
//                                         borderRadius: "10px"
//                                     }}
//                                     alt="Preview"
//                                     src={image}
//                                 />
//                             )}
//                         </Grid>
//                         <Grid item xs={12}>
//                             <Typography variant="caption" color="textSecondary">
//                                 Image requirements:
//                                 <br />
//                                 • Dimensions: {minWidth}x{minHeight}px to {maxWidth}x{maxHeight}px
//                                 <br />
//                                 • Max file size: {maxFileSize / (1024 * 1024)}MB
//                                 <br />
//                                 • Formats: PNG, JPEG, JPG, WebP
//                             </Typography>
//                         </Grid>
//                     </Grid>
//                 </DialogContent>
//                 <DialogActions>
//                     <Button type="submit" disabled={isSubmitting}>
//                         {isSubmitting ? (
//                             <>
//                                 <CircularProgress size={24} sx={{ marginRight: 1 }} />
//                                 Uploading...
//                             </>
//                         ) : (
//                             'Upload Image'
//                         )}
//                     </Button>
//                     <Button onClick={onClose} color="primary" disabled={isSubmitting}>
//                         Cancel
//                     </Button>
//                 </DialogActions>
//             </Box>
//         </Dialog>
//     );
// }